import { useReducer, useRef, useCallback, useEffect } from 'react'
import { RECONNECTION_CONFIG } from '../config/reconnection.config'

/**
 * Initial state for reconnection reducer with enhanced status field
 */
const initialState = {
  attempt: 1,
  timeLeft: RECONNECTION_CONFIG.COUNTDOWN_SECONDS,
  isReconnecting: false,
  showManualMessage: false,
  isActive: false,
  status: 'idle', // 'idle' | 'connecting' | 'waiting' | 'failed' | 'connected'
  error: null,
}

/**
 * Enhanced reconnection reducer with better state management
 */
const reconnectionReducer = (state, action) => {
  switch (action.type) {
    case 'START':
      return {
        ...state,
        attempt: 1,
        timeLeft: RECONNECTION_CONFIG.COUNTDOWN_SECONDS,
        isReconnecting: false,
        showManualMessage: false,
        isActive: true,
        status: 'connecting',
        error: null,
      }

    case 'SET_ATTEMPT':
      return {
        ...state,
        attempt: action.payload,
        status: 'connecting',
      }

    case 'SET_TIME_LEFT':
      return {
        ...state,
        timeLeft: action.payload,
        status: 'waiting',
      }

    case 'SET_RECONNECTING':
      return {
        ...state,
        isReconnecting: action.payload,
        status: action.payload ? 'connecting' : 'waiting',
      }

    case 'SET_MANUAL_MESSAGE':
      return {
        ...state,
        showManualMessage: action.payload,
        isReconnecting: false,
        isActive: false,
        status: 'failed',
        error: action.payload ? action.error : null,
      }

    case 'SET_CONNECTED':
      return {
        ...state,
        status: 'connected',
        isReconnecting: false,
        isActive: false,
      }

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        status: 'failed',
      }

    case 'STOP':
      return {
        ...state,
        isActive: false,
        isReconnecting: false,
        status: 'idle',
      }

    case 'RESET':
      return initialState

    default:
      return state
  }
}

/**
 * Utility function to create delay promise with AbortSignal support
 * @param {number} ms - Milliseconds to delay
 * @param {AbortSignal} signal - AbortSignal for cancellation
 * @returns {Promise<void>}
 */
const delay = (ms, signal = null) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      resolve()
    }, ms)

    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timeoutId)
        reject(new Error('Delay aborted'))
      })
    }
  })
}

/**
 * Enhanced useReconnection hook with robust error handling and AbortController
 * @param {Object} params - Hook parameters
 * @param {boolean} params.visible - Dialog visibility state
 * @param {boolean} params.isConnected - Connection status
 * @param {Function} params.onReconnect - Reconnection callback
 * @param {Function} params.onClose - Close callback
 * @param {Function} [params.onError] - Optional error callback
 * @returns {Object} Hook state and methods
 */
export const useReconnection = ({ visible, isConnected, onReconnect, onClose, onError }) => {
  const [state, dispatch] = useReducer(reconnectionReducer, initialState)

  // Refs for stable references and cleanup
  const abortControllerRef = useRef(null)
  const onReconnectRef = useRef(onReconnect)
  const onCloseRef = useRef(onClose)
  const onErrorRef = useRef(onError)
  const closeTimeoutRef = useRef(null)
  const isMountedRef = useRef(true)

  // Keep refs updated without causing re-renders
  useEffect(() => {
    onReconnectRef.current = onReconnect
  }, [onReconnect])

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      cleanup()
    }
  }, [])

  /**
   * Cleanup function to abort all ongoing operations
   */
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  /**
   * Enhanced reconnection process with AbortController and error handling
   */
  const startReconnectionProcess = useCallback(async () => {
    // Cleanup any existing process
    cleanup()

    // Create new AbortController for this process
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    try {
      for (
        let currentAttempt = 1;
        currentAttempt <= RECONNECTION_CONFIG.MAX_ATTEMPTS;
        currentAttempt++
      ) {
        // Check if process was aborted
        if (signal.aborted || !isMountedRef.current) {
          return
        }

        dispatch({ type: 'SET_ATTEMPT', payload: currentAttempt })
        dispatch({ type: 'SET_RECONNECTING', payload: true })

        // Attempt reconnection with error handling
        try {
          const reconnectResult = onReconnectRef.current()

          // Handle async onReconnect if it returns a promise
          if (reconnectResult && typeof reconnectResult.then === 'function') {
            await reconnectResult
          }
        } catch (error) {
          console.error(`Reconnection attempt ${currentAttempt} failed:`, error)

          // Call error callback if provided
          if (onErrorRef.current) {
            onErrorRef.current(error, currentAttempt)
          }

          // Dispatch error to state
          dispatch({
            type: 'SET_ERROR',
            payload: {
              message: error.message,
              attempt: currentAttempt,
              timestamp: Date.now(),
            },
          })
        }

        // Countdown with abort signal support
        for (let seconds = RECONNECTION_CONFIG.COUNTDOWN_SECONDS; seconds > 0; seconds--) {
          if (signal.aborted || !isMountedRef.current) {
            return
          }

          dispatch({ type: 'SET_TIME_LEFT', payload: seconds })
          await delay(1000, signal)
        }

        // If this was the last attempt, show manual message
        if (currentAttempt === RECONNECTION_CONFIG.MAX_ATTEMPTS) {
          dispatch({
            type: 'SET_MANUAL_MESSAGE',
            payload: true,
            error: {
              message: 'Connection failed after all attempts',
              attempts: RECONNECTION_CONFIG.MAX_ATTEMPTS,
              timestamp: Date.now(),
            },
          })
          return
        }
      }
    } catch (error) {
      // Handle critical errors
      console.error('Critical error in reconnection process:', error)

      if (onErrorRef.current) {
        onErrorRef.current(error)
      }

      dispatch({
        type: 'SET_MANUAL_MESSAGE',
        payload: true,
        error: {
          message: 'Critical error occurred',
          details: error.message,
          timestamp: Date.now(),
        },
      })
    }
  }, [cleanup])

  // Main effect: Start/stop reconnection process
  useEffect(() => {
    if (visible && !isConnected) {
      dispatch({ type: 'START' })
      startReconnectionProcess()
    } else if (!visible) {
      cleanup()
      dispatch({ type: 'STOP' })
    }
  }, [visible, isConnected, startReconnectionProcess, cleanup])

  // Effect: Handle successful connection with cancelable timeout
  useEffect(() => {
    if (isConnected && visible && state.isActive) {
      dispatch({ type: 'SET_CONNECTED' })

      // Clear any existing timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }

      // Set cancelable timeout for closing
      closeTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && onCloseRef.current) {
          onCloseRef.current()
        }
      }, RECONNECTION_CONFIG.SUCCESS_DELAY_MS)
    }
  }, [isConnected, visible, state.isActive])

  /**
   * Retry the reconnection process
   */
  const retry = useCallback(() => {
    dispatch({ type: 'START' })
    startReconnectionProcess()
  }, [startReconnectionProcess])

  /**
   * Reset the state to initial values
   */
  const reset = useCallback(() => {
    cleanup()
    dispatch({ type: 'RESET' })
  }, [cleanup])

  /**
   * Stop the reconnection process
   */
  const stop = useCallback(() => {
    cleanup()
    dispatch({ type: 'STOP' })
  }, [cleanup])

  return {
    state,
    retry,
    reset,
    stop,
  }
}
